function parsePositiveInt(raw: string | undefined, fallback: number): number {
  if (raw == null || raw === '') return fallback;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : fallback;
}

/**
 * Domain swap: prd = *.eureca.me, staging/canary = *.eureca.dev. The SAME
 * Cloudflare Pages bundle is served on app.eureca.me (prd) and app.eureca.dev
 * (canary), so resolve the BFF + SSO host from the serving domain at runtime —
 * a stale baked VITE_* never wins. localhost keeps the baked VITE_* values.
 * Keycloak mints host-matching issuers per domain, so keycloak-js matches.
 */
function onEurecaMe(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h === 'eureca.me' || h.endsWith('.eureca.me');
}

/**
 * Slots de QA (spec 086): hosts de um nível `qa<N>-<app>.eureca.dev` no mesmo
 * sufixo do canary. O slot-spas.sh embute o BFF (`qa<N>-candidate-api`) e o
 * Keycloak (`qa<N>-auth`) do próprio slot nos VITE_*, então o slot fica FORA do
 * `onEurecaDev` — senão o SPA do slot falaria com a stack canary.
 */
export function slotOfHostname(h: string): 'qa1' | 'qa2' | null {
  const m = /^(qa[12])-[a-z-]+\.eureca\.dev$/.exec(h);
  return m ? (m[1] as 'qa1' | 'qa2') : null;
}

/**
 * Efêmero por PR (spec 070): preview 1-nível `<app>-pr<N>.eureca.dev`. O
 * build-spas.sh embute o BFF (`candidate-api-pr<N>`) e o Keycloak (`auth-pr<N>`)
 * do PR nos VITE_*, então o efêmero também fica FORA do `onEurecaDev`. A
 * distinção é o LABEL `-pr<N>` — NUNCA um `endsWith('.eureca.dev')` cru, que
 * capturaria o PR e o mandaria para a stack canary (caça de bugs BR-1, spec 125).
 * Vale enquanto o efêmero por PR existir, ao lado da regra dos slots.
 */
export function isEphemeralHostname(h: string): boolean {
  return /-pr\d+\.eureca\.dev$/.test(h);
}

// Slot de QA: o silent-check DEVE ser same-origin (o /silent-check-sso.html é
// servido pelo nginx do slot).
function onSlot(): boolean {
  if (typeof window === 'undefined') return false;
  return slotOfHostname(window.location.hostname) !== null;
}

// Efêmero por PR: idem, servido pelo nginx do PR.
function onEphemeral(): boolean {
  if (typeof window === 'undefined') return false;
  return isEphemeralHostname(window.location.hostname);
}

function onEurecaDev(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return (h === 'eureca.dev' || h.endsWith('.eureca.dev')) && !onSlot() && !onEphemeral();
}

const API_URL = onEurecaMe()
  ? 'https://candidate-api.eureca.me'
  : onEurecaDev()
    ? 'https://candidate-api.eureca.dev'
    : import.meta.env.VITE_API_URL || 'http://localhost:3001';

const KEYCLOAK_URL = onEurecaMe()
  ? 'https://sso.eureca.me'
  : onEurecaDev()
    ? 'https://sso.eureca.dev'
    : import.meta.env.VITE_KEYCLOAK_URL;

// On the runtime-resolved stacks the silent-check page must be same-origin; the
// baked VITE value points elsewhere. Locally keep the baked value.
const SILENT_CHECK_SSO_REDIRECT_URI =
  onEurecaMe() || onEurecaDev() || onSlot() || onEphemeral()
    ? `${window.location.origin}/silent-check-sso.html`
    : import.meta.env.VITE_KEYCLOAK_SILENT_CHECK_SSO_REDIRECT_URI || undefined;

/** Parsed Vite env for the candidate SPA — single place to read `import.meta.env`. */
export const candidateEnv = {
  /** Short id (telemetry, logs) */
  appName: import.meta.env.VITE_APP_NAME || 'candidate',
  /** Window / document title */
  appDisplayName: import.meta.env.VITE_APP_DISPLAY_NAME || 'Eureca',
  /** Base URL of the Candidate BFF (runtime-resolved per serving domain) */
  apiUrl: API_URL,
  /**
   * Default timeout for BFF `fetch` (ms). `0` disables the SDK default timeout.
   * @see packages/candidate-api-sdk `fetchClient`
   */
  apiRequestTimeoutMs: parsePositiveInt(import.meta.env.VITE_API_REQUEST_TIMEOUT_MS, 30_000),
  /**
   * Ambiente de runtime (`dev`/`stg`/`prd`) — setado por-env via `VITE_APP_ENV` no
   * workflow de deploy. Carimbado como super-propriedade `env` no PostHog (projeto
   * único stg+prd). Default `dev` para builds locais. @see src/posthog.ts
   */
  appEnv: import.meta.env.VITE_APP_ENV || 'dev',
  /**
   * EU-747 — SHA curto do build (`VITE_BUILD_SHA`, injetado pelo workflow de
   * deploy). Identifica a implantação nos relatos de erro do router
   * (`POST /me/client-errors`): sem isso não dá para saber se a tela veio de um
   * build velho numa aba viva ou do build atual. `dev` em builds locais.
   */
  buildSha: import.meta.env.VITE_BUILD_SHA || 'dev',
  /**
   * GTM container de EVENTOS — recebe os eventos de conversão do dataLayer V2
   * (`sign_up`, `generate_lead`, …). Set por-env via `VITE_GTM_CONTAINER_ID`
   * (stg `GTM-KC3LT4PJ`); vazio/ausente desabilita a injeção desse container.
   */
  gtmContainerId: import.meta.env.VITE_GTM_CONTAINER_ID,
  /**
   * GTM container SÓ-CÓDIGO — presente no HTML para auto-tracking; o código não empurra nada por ele.
   * Set por-env via `VITE_GTM_CODE_CONTAINER_ID` (stg `GTM-TM4NZMTF`); vazio/ausente desabilita.
   */
  gtmCodeContainerId: import.meta.env.VITE_GTM_CODE_CONTAINER_ID,
  /**
   * Intercom Messenger — id do workspace (público, baked in at build time). Set por-env via
   * `VITE_INTERCOM_APP_ID`; vazio/ausente desabilita a messenger (boot vira no-op). @see src/intercom.ts
   */
  intercomAppId: import.meta.env.VITE_INTERCOM_APP_ID,
  /**
   * PostHog (product analytics + session replay, EU-331) — Project API Key pública,
   * baked in at build time. Set por-env via `VITE_POSTHOG_KEY`; vazio/ausente
   * desabilita o PostHog (boot vira no-op). @see src/posthog.ts
   */
  posthogKey: import.meta.env.VITE_POSTHOG_KEY,
  /**
   * Host do PostHog (US Cloud por default no boot). Set via `VITE_POSTHOG_HOST` só
   * se precisar apontar p/ outra região/instância.
   */
  posthogHost: import.meta.env.VITE_POSTHOG_HOST,
  // EU-506 — o loader do RD Station (`VITE_RD_STATION_LOADER_URL`) NÃO é lido em
  // runtime: é consumido no build pelo `vite-rd-station-plugin.ts`, que injeta o
  // <script> no <head> do index.html (o verificador do RD lê HTML estático).
  keycloak: {
    url: KEYCLOAK_URL,
    realm: import.meta.env.VITE_KEYCLOAK_REALM,
    clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
    onLoad:
      import.meta.env.VITE_KEYCLOAK_ON_LOAD === 'login-required'
        ? ('login-required' as const)
        : ('check-sso' as const),
    silentCheckSsoRedirectUri: SILENT_CHECK_SSO_REDIRECT_URI,
  },
  /**
   * Public pages for legal copy (EU-73). Override in `.env` when URLs are finalized.
   */
  legalTermsUrl: import.meta.env.VITE_LEGAL_TERMS_URL ?? 'https://www.eureca.me/termos-de-uso',
  legalPrivacyUrl:
    import.meta.env.VITE_LEGAL_PRIVACY_URL ?? 'https://www.eureca.me/politica-de-privacidade',
} as const;
